import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Overlay } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { ActionsMenuItem } from '../../common/ActionsMenu';
import { activateCustomer, deactivateCustomer, blacklistCustomer, unblacklistCustomer } from '../../../services/customerService';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../utils/rolePermissions';
import { customerActionsPopperConfig } from './LoanCustomers';
import { customerError, individualName, IndividualCustomer, restrictedIndividual } from './individualCustomer';

const statusActions = {
  activate: { label: 'Activate', icon: 'fa-user-check', run: activateCustomer },
  deactivate: { label: 'Deactivate', icon: 'fa-user-slash', run: deactivateCustomer },
  blacklist: { label: 'Blacklist', icon: 'fa-ban', run: blacklistCustomer },
  unblacklist: { label: 'Unblacklist', icon: 'fa-shield', run: unblacklistCustomer }
};

export default function IndividualCustomerActions({ customer }: { customer: IndividualCustomer }) {
  const navigate = useNavigate(), queries = useQueryClient();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [confirmation, setConfirmation] = useState<keyof typeof statusActions | null>(null);
  const [busy, setBusy] = useState(false);
  const [showMenu, setShowMenu] = useState(false), [measured, setMeasured] = useState(false);
  const target = useRef<HTMLButtonElement>(null);
  const closeMenu = () => { setShowMenu(false); setMeasured(false); };
  const items: (ActionsMenuItem & { className: string; dividerBefore?: boolean })[] = [
    { label: 'View Customer', icon: 'fa-eye', className: 'action-view', onClick: () => navigate(`/individual-customers/${customer.id}/view`) }
  ];
  if (hasPermission(Permission.CREATE_APPLICATION)) items.push({
    label: 'Apply for Loan', icon: 'fa-hand-holding-usd', className: 'action-apply-loan', disabled: restrictedIndividual(customer.status),
    hint: customer.status === 'INACTIVE' ? 'Customer is inactive' : 'Customer is blacklisted',
    onClick: () => navigate('/loan-application/new', { state: { idNumber: customer.info.idNumber,
      ...(customer.info.idType === 'PASSPORT' && {idType:customer.info.idType}),
      customerId: customer.id, customerNumber: customer.customerNumber, customerName: individualName(customer) } })
  });
  if (hasPermission(Permission.EDIT_CUSTOMER)) items.push({ label: 'Edit Customer', icon: 'fa-edit', className: 'action-edit', onClick: () => navigate(`/individual-customers/${customer.id}/edit`) });
  if (hasPermission(Permission.VIEW_CUSTOMER_DOCUMENTS)) items.push({ label: 'Documents', icon: 'fa-file', className: 'action-documents', onClick: () => navigate(`/individual-customers/${customer.id}/documents`) });
  if (customer.customerNumber && hasAnyPermission([Permission.VIEW_ACCOUNTS, Permission.VIEW_CUSTOMERS])) items.push({
    label: 'View Accounts', icon: 'fa-credit-card', className: 'action-accounts',
    onClick: () => navigate(`/customers/${customer.customerNumber}/accounts`)
  });

  if (hasPermission(Permission.EDIT_CUSTOMER)) {
    const actions: (keyof typeof statusActions)[] = customer.status === 'ACTIVE' ? ['deactivate', 'blacklist']
      : customer.status === 'INACTIVE' ? ['activate'] : ['SUSPENDED', 'BLACKLISTED'].includes(customer.status ?? '') ? ['unblacklist'] : [];
    actions.forEach((action, index) => items.push({ label: statusActions[action].label, icon: statusActions[action].icon,
      className: `action-${action}`, dividerBefore: index === 0, disabled: busy,
      onClick: () => setConfirmation(action) }));
    items.push({ label: 'Opening Balance', icon: 'fa-money', className: 'action-opening', dividerBefore: true,
      onClick: () => navigate(`/individual-customers/${customer.id}/opening-balance`) });
  }
  return <>
    <button ref={target} type="button" className="btn btn-sm btn-light rounded-pill d-flex align-items-center"
      aria-haspopup="menu" aria-expanded={showMenu} onClick={event => {
        event.preventDefault(); event.stopPropagation(); setMeasured(false); setShowMenu(value => !value);
      }}><span>Actions</span><i className="fa fa-caret-down ms-2" /></button>
    <Overlay show={showMenu} target={target.current} placement="bottom-end" rootClose onHide={closeMenu}
      popperConfig={{ ...customerActionsPopperConfig, onFirstUpdate: () => setMeasured(true) }}
      container={typeof document !== 'undefined' ? document.body : undefined}>
      {props => <div ref={props.ref} role="menu" className="dropdown-menu show actions-menu"
        aria-labelledby={props['aria-labelledby']} data-popper-placement={props.placement}
        style={{ ...props.style, display: 'block', visibility: measured ? 'visible' : 'hidden', opacity: measured ? 1 : 0 }}
        onClick={event => event.stopPropagation()} onKeyDown={event => {
          // This portalled menu has no Bootstrap toggle parent for the global keyboard handler.
          event.stopPropagation();
          if (event.key === 'Escape') { closeMenu(); target.current?.focus(); }
        }}>
        {items.map(item => <React.Fragment key={item.label}>
          {item.dividerBefore && <div className="dropdown-divider" role="separator" />}
          <button type="button" role="menuitem" className={`dropdown-item ${item.className}`} disabled={item.disabled}
            onClick={() => { item.onClick(); closeMenu(); }}>
            <i className={`fa ${item.icon}`} />{item.label}
            {item.disabled && item.hint && <div className="small text-muted">{item.hint}</div>}
          </button>
        </React.Fragment>)}
      </div>}
    </Overlay>
    <Modal show={!!confirmation} onHide={() => { if (!busy) setConfirmation(null); }} centered>
      <Modal.Header closeButton={!busy}><Modal.Title>{confirmation && statusActions[confirmation].label} Customer</Modal.Title></Modal.Header>
      <Modal.Body>{confirmation && statusActions[confirmation].label} {individualName(customer)}?</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => setConfirmation(null)}>Cancel</Button>
        <Button disabled={busy} onClick={async () => {
          if (!confirmation || !customer.id) return;
          setBusy(true);
          try {
            await statusActions[confirmation].run(customer.id);
            await Promise.all([
              queries.invalidateQueries({ queryKey: ['/customer/api/v1/search'] }),
              queries.invalidateQueries({ queryKey: [`/customer/api/v1/customers/${customer.id}`] })
            ]);
            toast.success('Customer status updated'); setConfirmation(null);
          } catch (failure) { toast.error(customerError(failure, 'Unable to update customer status')); }
          finally { setBusy(false); }
        }}>{busy ? 'Saving…' : 'Confirm'}</Button>
      </Modal.Footer>
    </Modal>
  </>;
}
